<template>
  <component :is="'style'" :id="styleIdConst">{{ orgCss }}</component>
  <Maintenance v-if="shouldShowMaintenance === true" />
  <div v-else>
  <div v-if="isLoadingScreenVisible === true" class="loading-overlay">
    <svg width="80" height="40" viewBox="0 0 120 40" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
      <circle cx="20" cy="20" r="8">
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0s"/>
      </circle>
      <circle cx="60" cy="20" r="8">
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0.2s"/>
      </circle>
      <circle cx="100" cy="20" r="8">
        <animateTransform attributeName="transform" type="translate" values="0,0;0,-15;0,0;0,0" keyTimes="0;0.3;0.6;1" calcMode="spline" keySplines="0.42 0 0.58 1;0.42 0 0.58 1;0.42 0 0.58 1" dur="1.2s" repeatCount="indefinite" begin="0.4s"/>
      </circle>
    </svg>
  </div>

  <template v-if="isLoadingScreenVisible === false">
    <div v-if="$route.meta.noLayout !== true && isLayoutHidden !== true">
      <div
        class="flex bg-gray-50 dark:bg-gray-900"
        :class="[
          { 'app-main-with-adi': adiWidgetOpen === true && isAdiFullPageRoute === false },
          { 'app-main-with-adi-rtl': adiWidgetOpen === true && isAdiFullPageRoute === false && isRTL === true },
          { 'light-app-shell': currentTheme === 'light' && showLightMenu === true },
          { 'light-menu-collapsed-shell': currentTheme === 'light' && showLightMenu === true && lightMenuExpanded !== true },
          { 'light-menu-rtl': currentTheme === 'light' && showLightMenu === true && isRTL === true }
        ]"
      >
        <LightMenu
          v-if="showLightMenu === true"
          @expandChange="onLightMenuExpandChange"
        />
        <div
          v-if="showTraditionalSidebar === true && $route.meta.hideNav!==true && $route.meta.hideSidebar !== true"
          class="lg:block"
          :class="{ 'lg:block hidden': sidebar!==true, block: sidebar === true }"
        >
          <div
            class="lg:flex-auto w-sidebar bg-white dark:bg-gray-800 border-r-2 dark:border-gray-700 lg:z-0 z-20 overflow-auto lg:relative fixed"
          >
            <perfect-scrollbar class="h-screen">
              <ModernSidebar
                v-if="$route.meta.hideNav!==true && currentTheme === 'modern'"
                @sidebarToggle="close"
              />
              <Sidebar
                v-else-if="$route.meta.hideNav!==true"
                @sidebarToggle="close"
              />
             
            </perfect-scrollbar>
          </div>
        </div>

        <div
          class="flex-auto w-full transition-all duration-300 min-w-0 light-app-content"
          :class="$route.meta.fillViewport === true ? 'h-screen overflow-hidden flex flex-col' : 'overflow-auto h-screen'"
          id="body-scroll"
        >
          <Header
            v-if="$route.meta.hideNav !== true && isEmbedded !== true"
            class="flex-shrink-0"
            @sidebarToggle="open"
            :show-logo="showSidebar === false || currentTheme === 'light'"
          />

          <transition
            name="slide-up"
            mode="out-in"
          >
          <router-view v-if="isLoadingScreenVisible === false" :class="$route.meta.fillViewport === true ? 'flex-1 min-h-0' : ''" />
          </transition>
          <TermsAndConditionsModal v-if="showTermsModal === true" @accepted="onTermsAccepted" @logout="performLogout" />
          <Footer
            v-if="$route.meta.hideFooter!==true"
            class="flex-shrink-0"
            :compact="$route.meta.fillViewport === true"
          />
        </div>
      </div>
      <AdiWidget v-if="adiWidgetOpen === true && isAdiFullPageRoute === false" @close="closeAdiWidget" />
    </div>
    <router-view v-else />
  </template>
  <PasswordChangeRequiredModal v-if="showPasswordChangeModal === true" @logout="performLogout" @passwordChanged="onPasswordChanged" />
  <div ref="dynamicScriptsTarget" id="dynamic-scripts-target-container"></div>
  <div v-if="isActivationModalVisible === true" class="activation-overlay">
    <div class="activation-modal">
      <h2 class="activation-title">Platform activation required</h2>
      <p class="activation-message">{{ activationStatusMessage }}</p>
      <div v-if="activationStatus !== null" class="activation-details">
        <p v-if="activationStatus.unlimited === true">Unlimited duration</p>
        <p v-else-if="activationStatus.expiresAt !== null">Expires at {{ formatActivationDate(activationStatus.expiresAt) }}</p>
        <p v-if="activationStatus.messageLimit !== null">Messages: {{ activationStatus.messageLimit }}</p>
        <p v-if="activationStatus.voiceMinutes !== null">Voice minutes: {{ activationStatus.voiceMinutes }}</p>
      </div>
      <button type="button" class="activation-button" @click="checkActivationStatus">Retry activation check</button>
    </div>
  </div>
  </div>
</template>

<script>

  import Sidebar from "@/components/Sidebar";
  import ModernSidebar from "@/Themes/modern/components/Sidebar.vue";
  import LightMenu from "@/Themes/light/components/LightMenu.vue";
  import Header from "@/components/Header";
  import Footer from "@/components/Footer";
  import AdiWidget from "@/components/AdiWidget.vue";
  import Maintenance from "@/views/layouts/error/maintenance.vue";
  import TermsAndConditionsModal from "@/components/TermsAndConditionsModal.vue";
  import PasswordChangeRequiredModal from "@/components/PasswordChangeRequiredModal.vue";
  import axios from '@/api/axiosInstance';
  import { useToast } from 'vue-toastification';
  
  import Scrollbar from "smooth-scrollbar";
  import {ref,watch, provide, computed, nextTick, getCurrentInstance, onMounted } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { globalState, updateOrgSettings, updateWhitelabelLogo, updateWhitelabelSettings, applyVendorBrandingFromCurrentOrigin } from '@/globalState';
  import { useI18n, applyTranslationsToDOM } from '@/i18n';
  import { initDB, loadOrgStyles, clearOrgStyles, saveOrgStyles } from '@/indexedDBService';
  import axiosInstance from '@/api/axiosInstance';
  import { fetchUserData, logoutSession } from '@/api/accountsService';
  import { getRuntimeThemeValue, getActiveThemeKey, normalizeThemeKey } from '@/Themes/themeRuntime.js';
  import smartLogger from '@/shared/tools/smartLogger.js';

  const log = smartLogger;
  const platformSettingsApplied = ref(false);
  
  function stringIncludesChatAgentV3(value) {
    if (typeof value === 'string' && value !== '') {
      const normalized = value.toLowerCase();
      if (normalized.includes('chatagentv3') === true) {
        return true;
      }
    }
    return false;
  }
  
  function isChatAgentV3Context(routeCandidate) {
    if (routeCandidate !== undefined && routeCandidate !== null) {
      const nameValue = routeCandidate.name;
      if (typeof nameValue === 'string' && nameValue !== '') {
        const normalizedName = nameValue.toLowerCase();
        if (normalizedName === 'chatagentv3') {
          return true;
        }
      }
      const pathValue = typeof routeCandidate.path === 'string' ? routeCandidate.path : '';
      if (stringIncludesChatAgentV3(pathValue) === true) {
        return true;
      }
      const fullPathValue = typeof routeCandidate.fullPath === 'string' ? routeCandidate.fullPath : '';
      if (stringIncludesChatAgentV3(fullPathValue) === true) {
        return true;
      }
    }
    if (typeof window !== 'undefined') {
      const hasLocation = typeof window.location !== 'undefined' && window.location !== null;
      if (hasLocation === true) {
        const pathNameValue = typeof window.location.pathname === 'string' ? window.location.pathname : '';
        if (stringIncludesChatAgentV3(pathNameValue) === true) {
          return true;
        }
        const hashValue = typeof window.location.hash === 'string' ? window.location.hash : '';
        if (stringIncludesChatAgentV3(hashValue) === true) {
          return true;
        }
        const searchValue = typeof window.location.search === 'string' ? window.location.search : '';
        if (stringIncludesChatAgentV3(searchValue) === true) {
          return true;
        }
      }
    }
    return false;
  }

  const applyGlobalPlatformSettings = (cssContent, jsContent) => {
    const styleId = 'platform-global-styles-app';
    let styleElement = document.getElementById(styleId);
    if (styleElement === null) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      if (document.head !== null && document.head !== undefined) {
        document.head.appendChild(styleElement);
      } else {
        log.error('applyGlobalPlatformSettings', 'App.vue: document.head not available to append platform styles.');
        return;
      }
    }
    styleElement.textContent = cssContent !== undefined && cssContent !== null ? cssContent : '';
    log.debug('applyGlobalPlatformSettings', 'App.vue: Applied global platform CSS.');

    const scriptId = 'platform-global-script-app';
    let scriptElement = document.getElementById(scriptId);
    if (scriptElement !== null && scriptElement !== undefined) {
      scriptElement.remove();
    }
    if (jsContent !== undefined && jsContent !== null && jsContent.trim().length > 0) {
      scriptElement = document.createElement('script');
      scriptElement.id = scriptId;
      scriptElement.textContent = jsContent;
      if (document.body !== null && document.body !== undefined) {
        document.body.appendChild(scriptElement);
        log.debug('applyGlobalPlatformSettings', 'App.vue: Applied global platform JS.');
      } else {
        log.error('applyGlobalPlatformSettings', 'App.vue: document.body not available to append platform script.');
      }
    }
  };

  const fetchAndApplyPlatformSettings = async () => {
    try {
      log.debug('fetchAndApplyPlatformSettings', 'App.vue: Fetching platform settings...');
      platformSettingsApplied.value = false; 
      const response = await fetch(`${globalState.apiEndpoint}/api/platformSettings`);
      if (response.ok === false) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      log.debug('fetchAndApplyPlatformSettings', 'App.vue: Platform settings fetched.');
      if (data !== null && data !== undefined) {
        const cssContentResolved = data.cssContent !== undefined ? data.cssContent : data.csscontent;
        const jsContentResolved = data.jsContent !== undefined ? data.jsContent : data.jscontent;
        applyGlobalPlatformSettings(cssContentResolved, jsContentResolved);
      }
    } catch (error) {
      log.error('fetchAndApplyPlatformSettings', 'App.vue: Error fetching or applying platform settings:', error);
      applyGlobalPlatformSettings('', ''); 
    } finally {
      platformSettingsApplied.value = true; 
    }
  };

  const userPermissions = computed(() => {
    const userInfo = globalState.userInfo;
    if (userInfo !== null && userInfo !== undefined && Array.isArray(userInfo.permissions) === true) {
      return userInfo.permissions;
    }
    return [];
  });

  const hasPermission = (requiredPermission) => {
    const permissions = userPermissions.value;
    return permissions.includes('fullAccessToOrganization') === true || permissions.includes(requiredPermission) === true;
  };

  function getCookie(name) {
    const cookies = document.cookie.split('; ');
    for (const cookie of cookies) {
      const [cookieName, cookieValue] = cookie.split('=');
      if (decodeURIComponent(cookieName) === name) {
        return decodeURIComponent(cookieValue);
      }
    }
    return null;
  }

  function isTokenExpired(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000;
      return Date.now() >= expirationTime;
    } catch (error) {
      return true;
    }
  }

  function parseJWTToken(token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return {
        userId: payload.userId,
        organizationID: payload.organizationID,
        organizationName: payload.organizationName,
        userName: payload.userName,
        permissions: payload.permissions != null && payload.permissions.length > 0 ? payload.permissions : [],
        roles: Array.isArray(payload.roles) === true ? payload.roles : []
      };
    } catch (error) {
      log.error('parseJWTToken', 'Error parsing JWT token:', error);
      return null;
    }
  }

  
  const useOrganizationStyles = (appCompletelyMountedRef) => {
    const route = useRoute();
    const orgCss = ref('');
    const orgJs = ref('');
    const orgStylesAppliedToDOM = ref(false);
    const orgScriptActuallyAppended = ref(false);
    const cssRenderedToDOM = ref(true); 
    const styleIdConst = 'organization-custom-styles-from-idb';
    const scriptId = 'organization-custom-script-from-idb';
    const dynamicScriptsTarget = ref(null); 

    let jsAppendPendingDueToMount = false;
    let pendingJsAppendWatcher = null;
    let loadStylesInFlight = null;

    const isExcludedRoute = () => {
      const path = typeof route.path === 'string' ? route.path.toLowerCase() : '';
      const excludedExactPaths = ['/login', '/termsandconditions', '/legal/dpa'];
      const excludedPrefixes = ['/embed/', '/aichatagent/', '/agent/', '/agents/', '/websiteai/', '/aichat/'];
      
      if (excludedExactPaths.includes(path) === true) {
        return true;
      }
      
      if (excludedPrefixes.some(prefix => path.startsWith(prefix) === true) === true) {
        return true;
      }

      if (route.name === 'Page404') {
        return true;
      }
      return false;
    };

    const applyOrgStylesToDOM = (css, js, targetElement) => {
      nextTick(async () => {
        if (isExcludedRoute() === true) {
          log.debug('applyOrgStylesToDOM', 'App.vue (Comp): Excluded route, ensuring org styles are removed from DOM.');
          removeOrgStylesFromDOM(targetElement);
          return;
        }

        if (document.body !== null) {
          const oldScript = document.getElementById(scriptId);
          if (oldScript !== null && oldScript.parentNode === document.body) {
            document.body.removeChild(oldScript);
          }

          const performJsAppendAction = () => {
            if (js !== undefined && js !== null && js.trim().length > 0) {
              orgScriptActuallyAppended.value = false;
              const scriptElement = document.createElement('script');
              scriptElement.id = scriptId;
              scriptElement.textContent = js;
              scriptElement.type = 'text/javascript';
              
              if (document.body !== null) {
                  document.body.appendChild(scriptElement);
                  orgScriptActuallyAppended.value = true;
                  log.debug('applyOrgStylesToDOM', 'App.vue (Comp): Applied actual organization JS directly to document.body (post-mount).');
              } else {
                  log.error('applyOrgStylesToDOM', 'App.vue (Comp) performJsAppendAction: document.body became null. Cannot append JS.');
                  orgScriptActuallyAppended.value = true;
              }
            } else {
              log.debug('applyOrgStylesToDOM', 'App.vue (Comp): No actual organization JS to apply or JS is empty.');
              orgScriptActuallyAppended.value = true;
            }
            jsAppendPendingDueToMount = false; 
          };

          if (appCompletelyMountedRef.value === true) {
            log.debug('applyOrgStylesToDOM', 'App.vue (Comp) applyOrgStylesToDOM: App is already mounted. Proceeding with JS append.');
            performJsAppendAction();
          } else {
            log.debug('applyOrgStylesToDOM', 'App.vue (Comp) applyOrgStylesToDOM: App not yet mounted. Scheduling JS append post-mount.');
            jsAppendPendingDueToMount = true;
            if (pendingJsAppendWatcher === null) {
              pendingJsAppendWatcher = watch(appCompletelyMountedRef, (isMounted) => {
                if (isMounted === true) {
                  if (jsAppendPendingDueToMount === true) {
                    log.debug('applyOrgStylesToDOM', 'App.vue (Comp) applyOrgStylesToDOM (watcher): App now mounted. Executing scheduled JS append.');
                    performJsAppendAction();
                  }
                  if (pendingJsAppendWatcher !== null) {
                    pendingJsAppendWatcher();
                    pendingJsAppendWatcher = null;
                  }
                }
              });
            }
          }
        } else {
          log.error('applyOrgStylesToDOM', 'App.vue (Comp) applyOrgStylesToDOM: document.body is null. Cannot append JS.');
          orgScriptActuallyAppended.value = true; 
        }
        
        orgStylesAppliedToDOM.value = (
          (css !== undefined && css !== null && css.trim().length > 0) ||
          (js !== undefined && js !== null && js.trim().length > 0)
        );
      });
    };

    const originalRemoveOrgStylesFromDOM = (targetElement) => {
      orgCss.value = ''; 
      cssRenderedToDOM.value = true; 
      const scriptFromBody = document.getElementById(scriptId);
      if (scriptFromBody !== null && scriptFromBody.parentNode === document.body) {
        document.body.removeChild(scriptFromBody);
        log.debug('originalRemoveOrgStylesFromDOM', 'App.vue (Comp): Cleared organization JS from document.body.');
      }
      if (targetElement !== null) {
        while (targetElement.firstChild !== null) {
          targetElement.removeChild(targetElement.firstChild);
        }
        log.debug('originalRemoveOrgStylesFromDOM', 'App.vue (Comp): Cleared organization JS from provided target element.');
      }
      orgStylesAppliedToDOM.value = false;
      orgScriptActuallyAppended.value = false;
    };

    const removeOrgStylesFromDOM = (targetElement) => {
      log.debug('removeOrgStylesFromDOM', 'App.vue (Comp) removeOrgStylesFromDOM: Removing styles.');
      originalRemoveOrgStylesFromDOM(targetElement);
      jsAppendPendingDueToMount = false; 
      if (pendingJsAppendWatcher !== null) {
          pendingJsAppendWatcher();
          pendingJsAppendWatcher = null;
      }
    };

    const loadAndApplyStyles = async (targetElement) => {
      log.debug('loadAndApplyStyles', 'App.vue (Comp): Attempting to load and apply styles.');
      if (isExcludedRoute() === true) {
          removeOrgStylesFromDOM(targetElement);
          orgCss.value = '';
          orgJs.value = '';
          cssRenderedToDOM.value = true;
          orgScriptActuallyAppended.value = true;
          return;
      }

      const userInfo = globalState.userInfo;
      const orgId = userInfo !== null && userInfo !== undefined ? userInfo.organizationID : null;
      if (orgId === null || orgId === undefined) {
        log.debug('loadAndApplyStyles', 'App.vue (Comp) loadAndApplyStyles: User not authenticated, clearing org styles.');
        await clearOrgStyles();
        removeOrgStylesFromDOM(targetElement);
        orgCss.value = '';
        orgJs.value = '';
        cssRenderedToDOM.value = true;
        orgScriptActuallyAppended.value = true;
        return;
      }
      
      let stylesFromIDB = null;
      try {
        stylesFromIDB = await loadOrgStyles();
      } catch (idbError) {
        log.error('loadAndApplyStyles', 'App.vue (Comp): Error loading styles from IndexedDB:', idbError);
        stylesFromIDB = null; 
      }

      let finalStyles = stylesFromIDB;

      if (finalStyles === null || finalStyles === undefined || (finalStyles.css === '' && finalStyles.js === '')) {
        log.debug('loadAndApplyStyles', 'App.vue (Comp): Styles not found in IDB or are empty. Attempting network fetch.');
        if (globalState.userInfo?.organizationID !== undefined && globalState.userInfo?.organizationID !== null) {
          if (loadStylesInFlight !== null) {
            try {
              finalStyles = await loadStylesInFlight;
            } catch (e) {
              finalStyles = { css: '', js: '' };
            }
          } else {
            loadStylesInFlight = (async () => {
              try {
                const response = await axiosInstance.get(`${globalState.apiEndpoint}/api/customDesigns`);
                if (response !== null && response !== undefined && response.data !== undefined) {
                  const resolved = { css: response.data.cssContent != undefined ? response.data.cssContent : '', js: response.data.jsContent != undefined ? response.data.jsContent : '' };
                  await saveOrgStyles(resolved);
                  log.debug('loadAndApplyStyles', 'App.vue (Comp): Fetched org styles from network and saved to IDB.');
                  return resolved;
                } else {
                  const empty = { css: '', js: '' };
                  await saveOrgStyles(empty);
                  return empty;
                }
              } catch (fetchError) {
                log.error('loadAndApplyStyles', 'App.vue (Comp): Error fetching org styles from network:', fetchError);
                const empty = { css: '', js: '' };
                await saveOrgStyles(empty);
                return empty;
              }
            })();
            try {
              finalStyles = await loadStylesInFlight;
            } finally {
              loadStylesInFlight = null;
            }
          }
        }
      }

      orgCss.value = finalStyles?.css != undefined ? finalStyles?.css : '';
      orgJs.value = finalStyles?.js != undefined ? finalStyles?.js : '';

      if (orgCss.value.trim().length > 0) {
        cssRenderedToDOM.value = false;
        nextTick(() => {
          cssRenderedToDOM.value = true;
          log.debug('loadAndApplyStyles', 'App.vue (Comp): CSS considered rendered to DOM.');
        });
      } else {
        cssRenderedToDOM.value = true; 
      }
      applyOrgStylesToDOM(orgCss.value, orgJs.value, targetElement);
    };

    return { orgCss, orgJs, orgStylesAppliedToDOM, loadAndApplyStyles, applyOrgStylesToDOM, removeOrgStylesFromDOM, isExcludedRoute, styleIdConst, dynamicScriptsTarget, orgScriptActuallyAppended, cssRenderedToDOM };
  };

  export default {
    name: "App",

    data() {
      return {
        sidebarDark: false,
        sidebar: false,
        userData: {
          userName: '',
          userID: null,
          organizationName: '',
          organizationID: null
        },
        isScrollbarInitialized: false,
        isMaintenanceMode: (window.env && window.env.VUE_APP_UPLOAD_VERSION_MODE !== undefined ? window.env.VUE_APP_UPLOAD_VERSION_MODE : process.env.VUE_APP_UPLOAD_VERSION_MODE) === 'true',
        showTermsModal: false,
        showPasswordChangeModal: false,
        isActivationModalVisible: false,
        activationStatus: null,
        activationStatusMessage: '',
        termsStatusCacheUserId: null,
        termsStatusCacheAccepted: null,
        termsStatusCacheAt: 0,
        termsStatusInFlight: null,
        userInfoInFlight: null
      };
    },

    components: {
      Header,
      Footer,
      Sidebar,
      ModernSidebar,
      LightMenu,
      Maintenance,
      TermsAndConditionsModal,
      PasswordChangeRequiredModal,
      AdiWidget
    },
    computed: { 
      showSidebar() {
        const result = hasPermission('accessSidebar');
        return result;
      },
      showLightMenu() {
        if (this.currentTheme !== 'light') {
          return false;
        }
        if (this.showSidebar !== true) {
          return false;
        }
        if (this.$route.meta.hideNav === true) {
          return false;
        }
        if (this.$route.meta.hideSidebar === true) {
          return false;
        }
        return true;
      },
      showTraditionalSidebar() {
        if (this.currentTheme === 'light') {
          return false;
        }
        return this.showSidebar === true;
      },
      shouldShowMaintenance() {
        if (this.isMaintenanceMode !== true) {
          return false;
        }
        if (this.$route.query.skipUploadVersionAlert === 'true') {
          return false;
        }
        if (this.$route.name === 'Login') {
          return false;
        }
        return true;
      },
    },
    methods: {
      formatActivationDate(timestamp) {
        if (timestamp === null || timestamp === undefined) {
          return '';
        }
        const date = new Date(timestamp);
        if (Number.isNaN(date.getTime()) === true) {
          return '';
        }
        return date.toLocaleString();
      },
      buildActivationMessage(status) {
        if (status === null) {
          return 'Activation status unavailable';
        }
        if (status.active === true) {
          return 'Platform activated';
        }
        if (status.reason !== undefined && status.reason !== null && status.reason !== '') {
          return `Platform inactive: ${status.reason}`;
        }
        return 'Platform not activated';
      },
      async checkActivationStatus() {
        if (this.shouldSkipTermsAndPlatform() === true) {
          this.isActivationModalVisible = false;
          return;
        }
        try {
          const response = await axios.get(`${globalState.apiEndpoint}/api/activation/status`);
          const raw = response !== null && response !== undefined ? response.data : null;
          const resolved = raw !== null && raw !== undefined && raw.active !== undefined ? raw : null;
          const details = resolved !== null && resolved.activation !== undefined ? resolved.activation : null;
          this.activationStatus = details;
          this.activationStatusMessage = this.buildActivationMessage(resolved);
          if (resolved !== null && resolved.active === true) {
            this.isActivationModalVisible = false;
          } else {
            this.isActivationModalVisible = true;
          }
        } catch (error) {
          const status = error !== undefined && error !== null && error.response !== undefined && error.response !== null ? error.response.status : null;
          if (status === 401 || status === 403) {
            this.activationStatus = null;
            this.activationStatusMessage = '';
            this.isActivationModalVisible = false;
            return;
          }
          if (error !== undefined && error !== null && error.response !== undefined && error.response !== null) {
            const data = error.response.data;
            const resolved = data !== undefined && data !== null && data.active !== undefined ? data : null;
            const details = resolved !== null && resolved.activation !== undefined ? resolved.activation : null;
            this.activationStatus = details;
            this.activationStatusMessage = this.buildActivationMessage(resolved);
          } else {
            this.activationStatus = null;
            this.activationStatusMessage = 'Activation status could not be loaded';
          }
          this.isActivationModalVisible = true;
        }
      },
      shouldSkipTermsAndPlatform() {
        const currentRoute = this.$route !== undefined && this.$route !== null ? this.$route : null;
        if (isChatAgentV3Context(currentRoute) === true) {
          return true;
        }
        if (this.isAnonymousAllowedRoute(currentRoute) === true) {
          return true;
        }
        if (currentRoute !== null) {
          const name = currentRoute.name;
          const path = typeof currentRoute.path === 'string' ? currentRoute.path : '';
          if (name === 'Login') {
            return true;
          }
          if (path !== null && path !== undefined) {
            const normalizedPath = path.toLowerCase();
            if (normalizedPath === '/login' || normalizedPath.startsWith('/login/')) {
              return true;
            }
          }
        } else if (typeof window !== 'undefined' && typeof window.location !== 'undefined') {
          const locPath = typeof window.location.pathname === 'string' ? window.location.pathname.toLowerCase() : '';
          if (locPath === '/login' || locPath.startsWith('/login/')) {
            return true;
          }
        }
        return false;
      },
      open() {
        this.sidebar = true;
      },
      close() {
        this.sidebar = false;
      },
      async checkTerms() {
        if (this.shouldSkipTermsAndPlatform() === true) {
          this.showTermsModal = false;
          return;
        }
        const rawUserId = globalState.userInfo !== null && globalState.userInfo !== undefined ? (globalState.userInfo.userID !== undefined ? globalState.userInfo.userID : globalState.userInfo.userId) : null;
        const userId = rawUserId !== undefined && rawUserId !== null ? String(rawUserId) : null;
        if (userId === null) {
          this.showTermsModal = false;
          return;
        }
        if (this.termsStatusCacheUserId === userId && this.termsStatusCacheAccepted === true && Date.now() - this.termsStatusCacheAt < 300000) {
          this.showTermsModal = false;
          return;
        }
        if (this.termsStatusInFlight !== null) {
          await this.termsStatusInFlight;
          return;
        }
        this.termsStatusInFlight = axios.get(`${globalState.apiEndpoint}/api/users/termsStatus`);
        try {
          const response = await this.termsStatusInFlight;
          const data = response !== undefined && response !== null && response.data !== undefined && response.data !== null ? response.data : {};
          const accepted = data.accepted === true;
          this.termsStatusCacheUserId = userId;
          this.termsStatusCacheAccepted = accepted;
          this.termsStatusCacheAt = Date.now();
          if (globalState.userInfo !== null && globalState.userInfo !== undefined) {
            globalState.userInfo.termsAccepted = accepted;
            globalState.userInfo.termsAcceptedAt = data.acceptedAt !== undefined && data.acceptedAt !== null ? data.acceptedAt : '';
            globalState.userInfo.termsVersion = data.version !== undefined && data.version !== null ? data.version : '';
            globalState.userInfo.currentTermsVersion = data.currentVersion !== undefined && data.currentVersion !== null ? data.currentVersion : '';
          }
          this.showTermsModal = accepted !== true;
        } catch (error) {
          this.showTermsModal = false;
        } finally {
          this.termsStatusInFlight = null;
        }
      },
      async onTermsAccepted(payload) {
        const rawUserId = globalState.userInfo !== null && globalState.userInfo !== undefined ? (globalState.userInfo.userID !== undefined ? globalState.userInfo.userID : globalState.userInfo.userId) : null;
        const userId = rawUserId !== undefined && rawUserId !== null ? String(rawUserId) : null;
        if (userId !== null) {
          this.termsStatusCacheUserId = userId;
          this.termsStatusCacheAccepted = true;
          this.termsStatusCacheAt = Date.now();
        }
        if (globalState.userInfo !== null && globalState.userInfo !== undefined) {
          globalState.userInfo.termsAccepted = true;
          globalState.userInfo.termsAcceptedAt = payload !== undefined && payload !== null && payload.acceptedAt !== undefined && payload.acceptedAt !== null ? payload.acceptedAt : new Date().toISOString();
          if (payload !== undefined && payload !== null && payload.version !== undefined) {
            globalState.userInfo.termsVersion = payload.version;
          }
        }
        this.showTermsModal = false;
      },
      checkPasswordChangeRequirement() {
        if (this.shouldSkipTermsAndPlatform() === true) {
          this.showPasswordChangeModal = false;
          return;
        }
        const userInfo = globalState.userInfo;
        this.showPasswordChangeModal = userInfo !== null && userInfo !== undefined && userInfo.passwordChangeRequired === true;
      },
      async onPasswordChanged() {
        this.showPasswordChangeModal = false;
        await this.performLogout();
      },
      async performLogout() {
        try {
          await logoutSession();
        } catch (e) {}
        const sameSiteLax = (process.env.environment === 'production') ? '; SameSite=Lax' : '';
        document.cookie = `authToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${sameSiteLax}`;
        document.cookie = `jwtToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${sameSiteLax}`;
        document.cookie = `userData=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/${sameSiteLax}`;
        globalState.userInfo = null;
        this.showPasswordChangeModal = false;
        this.$router.push('/login');
      },
      isAnonymousAllowedRoute(route) {
        const resolvedRoute = route !== undefined && route !== null ? route : {};
        const meta = resolvedRoute.meta;
        const isPublic = meta !== undefined && meta !== null && meta.requiresAuth === false;
        return isPublic === true;
      },
      checkAuth() {
        const currentRoute = this.$route !== undefined && this.$route !== null ? this.$route : (this.$router !== undefined && this.$router !== null && this.$router.currentRoute !== undefined ? this.$router.currentRoute.value : null);
        const allowAnonymous = this.isAnonymousAllowedRoute(currentRoute);
        if (allowAnonymous === true) {
          return;
        }

        const hasUserInfo = globalState.userInfo !== null && globalState.userInfo !== undefined;
        if (hasUserInfo === true) {
          this.checkPasswordChangeRequirement();
          return;
        }
        if (this.userInfoInFlight !== null) {
          return;
        }

        this.userInfoInFlight = (async () => {
          try {
            const userData = await fetchUserData();
            if (userData !== null && userData !== undefined) {
              const prevUserId = globalState.userInfo !== null && globalState.userInfo !== undefined ? (globalState.userInfo.userID !== undefined ? globalState.userInfo.userID : globalState.userInfo.userId) : null;
              globalState.userInfo = userData;
              globalState.userInfoValidatedAt = Date.now();
              const userId = userData.userID !== undefined && userData.userID !== null ? userData.userID : userData.userId;
              this.userData = {
                userName: userData.userName !== undefined && userData.userName !== null ? userData.userName : '',
                userID: userId,
                organizationName: userData.organizationName !== undefined && userData.organizationName !== null ? userData.organizationName : '',
                organizationID: userData.organizationID !== undefined && userData.organizationID !== null ? userData.organizationID : null
              };
              const newlyAuthenticated = prevUserId === null || String(prevUserId) !== String(userId);
              if (newlyAuthenticated === true) {
                globalState.triggerOrgStyleRefresh = Date.now();
              }
              this.checkPasswordChangeRequirement();
              return;
            }
          } catch (e) {}

          globalState.userInfo = null;
          globalState.userInfoValidatedAt = null;
        })().finally(() => {
          this.userInfoInFlight = null;
        });
      },
      applyThemeStyles() {
        const rawRuntimeTheme = getRuntimeThemeValue();
        const runtimeThemeTrim = typeof rawRuntimeTheme === 'string' ? rawRuntimeTheme.trim() : '';
        const runtimeTheme = runtimeThemeTrim.length > 0 && runtimeThemeTrim.indexOf('${') < 0 ? runtimeThemeTrim : null;
        const resolvedThemeValue = runtimeTheme !== null ? runtimeTheme : 'classic';
        const normalizedThemeValue = normalizeThemeKey(resolvedThemeValue);
        let hrefValue = resolvedThemeValue;
        if (normalizedThemeValue === 'modern') {
          hrefValue = '/css/modern-theme.css';
        } else if (resolvedThemeValue.endsWith('-theme.css') === false && resolvedThemeValue.includes('://') === false && resolvedThemeValue.startsWith('/') === false) {
          hrefValue = `/css/${resolvedThemeValue}-theme.css`;
        }
        if (typeof document === 'undefined') {
          return;
        }
        const headEl = document.head;
        if (headEl === null || headEl === undefined) {
          return;
        }
        const linkId = 'platform-theme-styles';
        let linkEl = document.getElementById(linkId);
        if (linkEl === null) {
          linkEl = document.createElement('link');
          linkEl.id = linkId;
          linkEl.rel = 'stylesheet';
          headEl.appendChild(linkEl);
        }
        linkEl.href = hrefValue;
      },
      async applyOrganizationCustomCode() {
        if (this.orgStylesAppliedToDOM === true) {
            return;
        }
        log.debug('applyOrganizationCustomCode', 'App.vue (Options API): Attempting API fetch as fallback.');
       
        try {
          const response = await axios.get(`${globalState.apiEndpoint}/api/customDesigns`);
          
          const jsContent = response.data.jsContent;

          if (jsContent !== undefined && jsContent !== null && jsContent.trim().length > 0) {
            log.debug('applyOrganizationCustomCode', 'App.vue (Options API): Fetched JS via API fallback.');
            this.pendingOrgJs = jsContent;
          } else {
            this.pendingOrgJs = null;
          }

        } catch (error) {
          log.error('applyOrganizationCustomCode', 'App.vue (Options API): Error loading custom organization code via API fallback:', error);
        } finally {
          globalState.isCustomCodeApplied = true; 
        }
      },
      async applyPlatformSettings() {
        if (this.shouldSkipTermsAndPlatform() === true) {
          this.isPlatformCodeApplied = true;
          return;
        }
        if (this.$route.path.includes('/aiChatAgent/') === true) {
          this.isPlatformCodeApplied = true;
          return;
        }
        if (this.isPlatformCodeApplied === true && this.$route.name !== 'Login' && this.$route.name !== undefined) {
          return;
        }
        log.debug('applyPlatformSettings', 'App.vue applyPlatformSettings: Proceeding to fetch.');
        try {
          const response = await axios.get(`${globalState.apiEndpoint}/api/platformSettings`);
          await this.$nextTick();

          const cssContent = response.data.cssContent;
          const jsContent = response.data.jsContent;

          if (cssContent !== undefined && cssContent !== null && cssContent.trim().length > 0) {
            const styleId = 'platform-global-styles';
            let styleElement = document.getElementById(styleId);
            if (styleElement === null) {
              styleElement = document.createElement('style');
              styleElement.id = styleId;
              if (document.head !== null) {
                document.head.appendChild(styleElement);
              } else {
                log.error('applyPlatformSettings', 'App.vue applyPlatformSettings: document.head is null for CSS');
                this.isPlatformCodeApplied = true; 
                return;
              }
            }
            styleElement.textContent = cssContent;
            log.debug('applyPlatformSettings', 'App.vue: Applied platform global CSS to <head>');
          }

          if (jsContent !== undefined && jsContent !== null && jsContent.trim().length > 0) {
            this.pendingPlatformJs = jsContent;
            log.debug('applyPlatformSettings', 'App.vue: Stored platform JS for deferred application.');
          } else {
            this.pendingPlatformJs = null;
          }

        } catch (error) {
          log.error('applyPlatformSettings', 'App.vue: Error loading platform settings:', error);
        } finally {
          this.isPlatformCodeApplied = true;
        }
      },
      applyPendingJsScripts() {
        this.$nextTick(() => {
          
          if (this.pendingPlatformJs !== null && this.pendingPlatformJs.trim().length > 0) {
            const scriptId = 'platform-global-script';
            let scriptElement = document.getElementById(scriptId);
            if (scriptElement !== null) {
              scriptElement.remove();
            }
            scriptElement = document.createElement('script');
            scriptElement.id = scriptId;
            scriptElement.textContent = this.pendingPlatformJs;
            if (document.body !== null) {
              document.body.appendChild(scriptElement);
              log.debug('applyPendingJsScripts', 'App.vue: Applied platform global JS to <body> (deferred)');
            } else {
              log.error('applyPendingJsScripts', 'App.vue applyPendingJsScripts: document.body is null for platform JS');
            }
            this.pendingPlatformJs = null;
          }

          
          if (this.pendingOrgJs !== null && this.pendingOrgJs.trim().length > 0) {
            const scriptId = 'organization-custom-script'; 
            const compAPIScript = document.getElementById('organization-custom-script-from-idb');
            if (compAPIScript !== null && compAPIScript.textContent === this.pendingOrgJs) {
                log.debug('applyPendingJsScripts', 'App.vue applyPendingJsScripts: Org JS already applied by Composition API.');
                this.pendingOrgJs = null;
                return;
            }
            
            let scriptElement = document.getElementById(scriptId);
            if (scriptElement !== null) {
              scriptElement.remove();
            }
            scriptElement = document.createElement('script');
            scriptElement.id = scriptId;
            scriptElement.textContent = this.pendingOrgJs;
            if (document.body !== null) {
              document.body.appendChild(scriptElement);
              log.debug('applyPendingJsScripts', 'App.vue applyPendingJsScripts: Applied custom organization JS to <body>.');
            } else {
              log.error('applyPendingJsScripts', 'App.vue applyPendingJsScripts: document.body is null for organization JS.');
            }
            this.pendingOrgJs = null;
          }
        });
      }
    },
    watch: {
      $route(to, from) {
        this.sidebar = false;
        this.applyThemeStyles();
        this.checkAuth();
        this.checkActivationStatus().catch(() => {});
        this.checkTerms().catch(() => {});
        this.checkPasswordChangeRequirement();
        log.debug('routeWatcher', `App.vue $route watcher: Route changed to: ${to.path}`);
      },
      isLoadingScreenVisible(newValue) {
        if (newValue === false) { 
          this.$nextTick(() => {
            if (this.isScrollbarInitialized === false) {
              const bodyScrollElement = document.querySelector("#body-scroll");
              if (bodyScrollElement !== null) {
                Scrollbar.init(bodyScrollElement);
                this.isScrollbarInitialized = true;
                log.debug('loadingWatcher', 'App.vue: SmoothScrollbar initialized.');
              } else {
                if (this.$route.meta.noLayout !== true && this.isLayoutHidden !== true) {
                  log.error('loadingWatcher', 'App.vue: #body-scroll element not found for Scrollbar.init');
                } else {
                  log.debug('loadingWatcher', 'App.vue: #body-scroll element not found, but layout is hidden/noLayout');
                }
              }
            }
            this.applyPendingJsScripts();
          });
        }
      }
    },
    mounted() {
      this.isPlatformCodeApplied = false; 
      this.isScrollbarInitialized = false; 
      this.pendingPlatformJs = null;
      this.pendingOrgJs = null;
      this.applyThemeStyles();

      
      if (globalState.whitelabel === undefined || globalState.whitelabel === null) {
        globalState.whitelabel = {};
      }

      const whiteLabelNameFromEnv = process.env.VUE_APP_WHITELABEL_NAME;
      if (whiteLabelNameFromEnv !== undefined && whiteLabelNameFromEnv !== null && whiteLabelNameFromEnv.trim().length > 0) {
        globalState.whitelabel.appName = whiteLabelNameFromEnv;
      } else {
        if (globalState.whitelabel.appName === undefined) {
             globalState.whitelabel.appName = null; 
        }
      }

  
      const currentLogo = globalState.whitelabel !== undefined && globalState.whitelabel !== null ? globalState.whitelabel.logoUrl : null;
      updateWhitelabelLogo(currentLogo);
      applyVendorBrandingFromCurrentOrigin().catch(() => {});

      this.checkActivationStatus().catch(() => {});
      this.checkAuth();
      this.checkTerms().catch(() => {});
      this.checkPasswordChangeRequirement();
    },
    setup() {
      const { currentLanguage, setCurrentLanguage, t, availableLanguages } = useI18n();
      const isRTL = computed(() => {
        const lang = availableLanguages.value.find(function(entry) { return entry.code === currentLanguage.value; });
        return lang !== undefined && lang !== null && lang.dir === 'rtl';
      });
      const route = useRoute();
      const router = useRouter();
      const isChatAgentV3DirectView = computed(() => {
        return isChatAgentV3Context(route);
      });
      const shouldSkipPlatformFetch = () => {
        const currentPath = typeof route.path === 'string' ? route.path.toLowerCase() : '';
        if (currentPath === '/login') {
          return true;
        }
        if (currentPath === '/termsandconditions') {
          return true;
        }
        if (currentPath === '/legal/dpa') {
          return true;
        }
        if (currentPath.includes('/aichatagent/') === true) {
          return true;
        }
        if (isChatAgentV3DirectView.value === true) {
          return true;
        }
        return false;
      };

      const appCompletelyMounted = ref(false);
      const adiWidgetOpen = ref(false);

      const toggleAdiWidget = () => {
        adiWidgetOpen.value = adiWidgetOpen.value === false;
      };

      const closeAdiWidget = () => {
        adiWidgetOpen.value = false;
      };

      provide('toggleAdiWidget', toggleAdiWidget);
      provide('closeAdiWidget', closeAdiWidget);
      provide('adiWidgetOpen', adiWidgetOpen);

      const isAdiFullPageRoute = computed(() => {
        return route.path === '/chat';
      });

      watch(() => route.path, (nextPath) => {
        if (nextPath === '/chat') {
          closeAdiWidget();
        }
      });

      const { orgCss, orgJs, orgStylesAppliedToDOM, loadAndApplyStyles, applyOrgStylesToDOM, removeOrgStylesFromDOM, isExcludedRoute, styleIdConst, dynamicScriptsTarget, orgScriptActuallyAppended, cssRenderedToDOM } = useOrganizationStyles(appCompletelyMounted);
   
      const instance = getCurrentInstance();
      if (instance !== null && instance.appContext.config.globalProperties !== undefined) {
        instance.appContext.config.globalProperties.orgStylesAppliedToDOM = orgStylesAppliedToDOM;
      }
     
      onMounted(async () => {
        const mountWatchdogId = window.setTimeout(() => {
          if (appCompletelyMounted.value === false) {
            appCompletelyMounted.value = true;
            log.error('onMounted', 'App.vue initialization exceeded four seconds. Rendering the application while initialization continues.');
          }
        }, 4000);
        try {
          const skipPlatformFetch = shouldSkipPlatformFetch();
          if (skipPlatformFetch === true) {
            platformSettingsApplied.value = true;
            log.debug('onMounted', 'App.vue (Setup) onMounted: Skipped platform fetch.');
            return;
          }
          await initDB();
          await fetchAndApplyPlatformSettings();
          
          if (isExcludedRoute() === false) {
            await loadAndApplyStyles(dynamicScriptsTarget.value);
          }
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          log.error('onMounted', `App.vue initialization failed: ${errorMessage}`);
        } finally {
          window.clearTimeout(mountWatchdogId);
          await nextTick();
          appCompletelyMounted.value = true;
          log.debug('onMounted', 'App.vue (Setup) onMounted: appCompletelyMounted set to true.');
        }
      });

      watch(dynamicScriptsTarget, async (newValue, oldValue) => {
        if (newValue !== null && oldValue === null) { 
          log.debug('dynamicScriptsTargetWatcher', 'App.vue (Comp) watcher: dynamicScriptsTarget is now populated.');
          await nextTick(); 
          await loadAndApplyStyles(dynamicScriptsTarget.value);
        }
      }, { immediate: true });

      watch(() => globalState.triggerOrgStyleRefresh, async (newValue, oldValue) => {
        if (newValue !== null && newValue !== oldValue) {
            log.debug('triggerOrgStyleRefreshWatcher', `App.vue (Comp) watcher: triggerOrgStyleRefresh changed to ${newValue}`);
            await loadAndApplyStyles(dynamicScriptsTarget.value);
        }
      });

      watch(() => globalState.userInfo, async (newUserInfo, oldUserInfo) => {
        if (newUserInfo !== null && newUserInfo !== undefined) {
          const organizationId = newUserInfo.organizationID !== undefined && newUserInfo.organizationID !== null ? newUserInfo.organizationID : newUserInfo.organizationId;
          if (organizationId !== undefined && organizationId !== null) {
            try {
              const settingsResponse = await axiosInstance.get(`${globalState.apiEndpoint}/api/organizationSettings/public/${organizationId}`);
              if (settingsResponse.data !== undefined && settingsResponse.data !== null) {
                updateOrgSettings(settingsResponse.data.timezone, settingsResponse.data.language, settingsResponse.data.allowedSources);
                updateWhitelabelSettings(settingsResponse.data.whiteLabelEnabled === true, settingsResponse.data.whiteLabelLogoUrl, settingsResponse.data.whiteLabelAppName);
                applyVendorBrandingFromCurrentOrigin().catch(() => {});
              }
            } catch (e) {
              updateWhitelabelSettings(false, null);
              applyVendorBrandingFromCurrentOrigin().catch(() => {});
            }
          }
        }
        if (newUserInfo === null && oldUserInfo !== null) {
          log.debug('userInfoWatcher', 'App.vue (Comp) watcher: User logged out.');
          updateWhitelabelSettings(false, null);
          await clearOrgStyles();
          orgCss.value = '';
          orgJs.value = '';
          removeOrgStylesFromDOM(dynamicScriptsTarget.value);
        }
      }, { immediate: true });

      watch(() => route.path, async (newPath, oldPath) => {
        log.debug('pathWatcher', `App.vue (Comp) watcher: route.path changed to ${newPath}`);
        if (isExcludedRoute() === true) {
          if (orgStylesAppliedToDOM.value === true) {
            removeOrgStylesFromDOM(dynamicScriptsTarget.value);
          }
        } else {
          if (orgCss.value.length > 0 || orgJs.value.length > 0) {
            applyOrgStylesToDOM(orgCss.value, orgJs.value, dynamicScriptsTarget.value);
          } else {
            await loadAndApplyStyles(dynamicScriptsTarget.value); 
          }
        }
      });
      watch(() => route.query.lang, async (newLang, oldLang) => {
        if (typeof newLang === 'string' && newLang !== undefined && newLang !== null && newLang !== '' && newLang !== oldLang && newLang !== 'en') {
          await setCurrentLanguage(newLang);
        }
      }, { immediate: true });
      

      const isLayoutHidden = computed(() => route.meta.noLayout === true && route.path.startsWith('/embed/')===true);

      const hideSidebar = ref(false);
      provide('hideSidebar', hideSidebar);
      const hideFooter = ref(false);
      provide('hideFooter', hideFooter);

      const isCustomCodeApplied_inSetup = orgStylesAppliedToDOM;
      
      const isLoadingScreenVisible = computed(() => {
        if (appCompletelyMounted.value === true) {
          return false;
        }

        if (isExcludedRoute() === true) {
          return platformSettingsApplied.value === false; 
        }

        const platformDone = platformSettingsApplied.value === true;
        const orgCssProcessed = cssRenderedToDOM.value === true;

        const orgJsContentExists = orgJs.value !== undefined && orgJs.value !== null && orgJs.value.trim().length > 0;
        let orgJsProcessed = true; 
        if (orgJsContentExists === true) {
          orgJsProcessed = orgScriptActuallyAppended.value === true;
        }
        
        const doneLoading = platformDone === true && orgCssProcessed === true && orgJsProcessed === true;
        return doneLoading === false;
      });
      
      const isEmbedded = computed(() => {
        return route.query.embedded === 'true';
      });

      const currentTheme = computed(() => getActiveThemeKey());
      const lightMenuExpanded = ref(true);

      function onLightMenuExpandChange(value) {
        lightMenuExpanded.value = value === true;
      }

      return { 
        isRTL,
        isLayoutHidden,
        hideSidebar,
        hideFooter,
        t,
        orgCss, 
        orgJs,
        orgStylesAppliedToDOM,
        isEmbedded,
        isLoadingScreenVisible,
        styleIdConst,
        dynamicScriptsTarget,
        orgScriptActuallyAppended,
        cssRenderedToDOM,
        currentTheme,
        lightMenuExpanded,
        onLightMenuExpandChange,
        adiWidgetOpen,
        closeAdiWidget,
        isAdiFullPageRoute
      };
    },
    created() {
      this.toast = useToast();
    },
  };
</script>

<style>

 
 

  .slide-up-enter-active {
    transition: all 0.3s ease-out;
  }

  .slide-up-leave-active {
    transition: all 0.8s cubic-bezier(1, 0.5, 0.8, 1);
  }
  tr:first-child th:first-child {
    border-top-left-radius: 11px;
}

tr:last-child th:last-child {
    border-top-right-radius: 11px;
}
  .slide-up-enter-from,
  .slide-up-leave-to {
    transform: translateY(20px);
    opacity: 0;
  }

.layout-rtl {
  flex-direction: row-reverse;
}

.app-main-with-adi {
  padding-right: var(--adi-panel-width, min(400px, 38vw));
  box-sizing: border-box;
  transition: padding-right 0.15s ease;
}

.app-main-with-adi.app-main-with-adi-rtl {
  padding-right: 0;
  padding-left: var(--adi-panel-width, min(400px, 38vw));
  transition: padding-left 0.15s ease;
}

.light-app-shell {
  --light-menu-width: 240px;
  background: #f8fafc !important;
}

.light-app-shell.light-menu-collapsed-shell {
  --light-menu-width: 72px;
}

.light-app-shell .light-app-content {
  margin-left: var(--light-menu-width);
  transition: margin-left 0.22s ease, margin-right 0.22s ease;
}

.light-app-shell.light-menu-rtl .light-app-content {
  margin-left: 0;
  margin-right: var(--light-menu-width);
}

.dir-rtl {
  direction: rtl;
}

html[dir='rtl'] .space-x-1 > :not([hidden]) ~ :not([hidden]) {
  margin-right: 0.25rem;
  margin-left: 0;
}

html[dir='rtl'] .space-x-2 > :not([hidden]) ~ :not([hidden]) {
  margin-right: 0.5rem;
  margin-left: 0;
}

html[dir='rtl'] .space-x-3 > :not([hidden]) ~ :not([hidden]) {
  margin-right: 0.75rem;
  margin-left: 0;
}

html[dir='rtl'] .space-x-4 > :not([hidden]) ~ :not([hidden]) {
  margin-right: 1rem;
  margin-left: 0;
}

html[dir='rtl'] button.mr-2,
html[dir='rtl'] .btn.mr-2 {
  margin-right: 0;
  margin-left: 0.5rem;
}

html[dir='rtl'] button.ml-2,
html[dir='rtl'] .btn.ml-2 {
  margin-left: 0;
  margin-right: 0.5rem;
}

html[lang='he'] body,
html[lang='he'] button,
html[lang='he'] input,
html[lang='he'] select,
html[lang='he'] textarea {
  font-family: "Heebo", sans-serif;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  color: #4a00ff;
}

.activation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 16px;
}

.activation-modal {
  background: #ffffff;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
  max-width: 420px;
  width: 100%;
  text-align: center;
}

.activation-title {
  margin: 0 0 8px 0;
  font-size: 20px;
  color: #111827;
}

.activation-message {
  margin: 0 0 10px 0;
  color: #374151;
  font-size: 15px;
}

.activation-details p {
  margin: 4px 0;
  color: #4b5563;
  font-size: 14px;
}

.activation-button {
  margin-top: 14px;
  background: #4a00ff;
  color: #ffffff;
  border: none;
  padding: 10px 14px;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
  width: 100%;
}

.activation-button:hover {
  background: #3c00cc;
}

</style>
